import { formatTimerTime } from './practice-timer.js';
import { renderTimerDisplay, timerDisplayTemplate } from './timer-display.js';
import {
  TIMER_CUBE_ORDERS,
  deleteTimerHistoryRecord,
  formatHistoryDate,
  getTimerHistoryRecord,
  parseTimerHistoryTime,
  toDateTimeLocalValue,
  updateTimerHistoryRecord,
} from './timer-history.js';

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function playerName(value, fallback) {
  return String(value ?? '').trim() || fallback;
}

export function historyDetailTemplate(record) {
  if (!record) {
    return `<section class="timer-detail-page timer-detail-missing" aria-labelledby="history-detail-title">
      <div class="timer-detail-card">
        <p class="eyebrow">历史记录</p>
        <h1 id="history-detail-title">找不到这条记录</h1>
        <p>它可能已经被删除，或当前链接已经失效。</p>
        <button class="primary-button" type="button" data-history-detail-action="back">返回历史记录</button>
      </div>
    </section>`;
  }

  if (record.mode === 'dual') {
    const player1 = playerName(record.player1, '玩家1');
    const player2 = playerName(record.player2, '玩家2');
    return `<section class="timer-detail-page" aria-labelledby="history-detail-title">
      <div class="timer-detail-heading">
        <button class="back-button" type="button" data-history-detail-action="back"><span aria-hidden="true">←</span> 返回历史记录</button>
        <div><p class="eyebrow">双人比赛</p><h1 id="history-detail-title">第 ${record.serial} 条历史记录</h1></div>
      </div>

      <div class="timer-detail-card dual-history-detail">
        <div class="dual-history-displays">
          <section><h2>${escapeHtml(player1)}</h2>${timerDisplayTemplate({ className: 'timer-detail-display', state: 'stopped' })}</section>
          <section><h2>${escapeHtml(player2)}</h2>${timerDisplayTemplate({ className: 'timer-detail-display', state: 'stopped' })}</section>
        </div>
        <dl class="timer-detail-data dual-history-data">
          <div><dt>序号</dt><dd>#${record.serial}</dd></div>
          <div><dt>比赛模式</dt><dd>双人模式</dd></div>
          <div><dt>阶数</dt><dd>${record.order}×${record.order}</dd></div>
          <div><dt>日期时间</dt><dd>${formatHistoryDate(record.recordedAt)}</dd></div>
          <div><dt>获胜者</dt><dd class="dual-history-winner">${escapeHtml(record.winner)}</dd></div>
        </dl>
        <div class="timer-detail-actions">
          <button class="secondary-button" type="button" data-history-detail-action="back">返回历史记录</button>
          <button class="secondary-button" type="button" data-history-detail-action="edit">修改记录</button>
          <button class="danger-button" type="button" data-history-detail-action="request-delete">删除记录</button>
        </div>
      </div>

      <dialog class="confirm-dialog history-edit-dialog" data-history-edit-dialog aria-labelledby="history-edit-title">
        <form data-history-edit-form>
          <div class="dialog-header">
            <div><p class="eyebrow">编辑双人比赛</p><h2 id="history-edit-title">修改记录</h2></div>
            <button class="icon-button" type="button" data-history-detail-action="cancel-edit" aria-label="关闭">×</button>
          </div>
          <div class="history-edit-fields dual-history-edit-fields">
            <label><span>玩家1名称</span><input type="text" maxlength="80" value="${escapeHtml(record.player1)}" data-history-edit-player1></label>
            <label><span>玩家2名称</span><input type="text" maxlength="80" value="${escapeHtml(record.player2)}" data-history-edit-player2></label>
            <label><span>玩家1成绩</span><input type="text" inputmode="numeric" value="${formatTimerTime(record.time1)}" placeholder="00:00.000" pattern="[0-9]{2}:[0-9]{2}\.[0-9]{3}" data-history-edit-time1 required></label>
            <label><span>玩家2成绩</span><input type="text" inputmode="numeric" value="${formatTimerTime(record.time2)}" placeholder="00:00.000" pattern="[0-9]{2}:[0-9]{2}\.[0-9]{3}" data-history-edit-time2 required></label>
            <label><span>阶数</span><select data-history-edit-order>${TIMER_CUBE_ORDERS.map((order) => `<option value="${order}"${order === record.order ? ' selected' : ''}>${order}×${order}</option>`).join('')}</select></label>
            <label><span>日期时间</span><input type="datetime-local" step="1" value="${toDateTimeLocalValue(record.recordedAt)}" data-history-edit-date required></label>
          </div>
          <p class="history-edit-error" data-history-edit-error hidden></p>
          <div class="confirm-actions"><button class="secondary-button" type="button" data-history-detail-action="cancel-edit">取消</button><button class="primary-button" type="submit">保存修改</button></div>
        </form>
      </dialog>

      <dialog class="confirm-dialog danger-confirm" data-history-delete-dialog aria-labelledby="history-delete-title">
        <h2 id="history-delete-title">确认删除这条双人比赛记录？</h2>
        <p>第 ${record.serial} 条记录及其双方成绩将从此浏览器删除，此操作无法撤销。</p>
        <div class="confirm-actions"><button class="secondary-button" type="button" data-history-detail-action="cancel-delete">取消</button><button class="danger-button" type="button" data-history-detail-action="confirm-delete">确认删除</button></div>
      </dialog>
    </section>`;
  }

  return `<section class="timer-detail-page" aria-labelledby="history-detail-title">
    <div class="timer-detail-heading">
      <button class="back-button" type="button" data-history-detail-action="back"><span aria-hidden="true">←</span> 返回历史记录</button>
      <div><p class="eyebrow">计时成绩</p><h1 id="history-detail-title">第 ${record.serial} 条历史记录</h1></div>
    </div>

    <div class="timer-detail-card">
      ${timerDisplayTemplate({ className: 'timer-detail-display', state: 'stopped' })}
      <dl class="timer-detail-data">
        <div><dt>序号</dt><dd>#${record.serial}</dd></div>
        <div><dt>用时</dt><dd>${formatTimerTime(record.elapsedMs)}</dd></div>
        <div><dt>阶数</dt><dd>${record.order}×${record.order}</dd></div>
        <div><dt>日期时间</dt><dd>${formatHistoryDate(record.recordedAt)}</dd></div>
        <div><dt>用户名</dt><dd>${escapeHtml(record.username)}</dd></div>
      </dl>
      <div class="timer-detail-actions">
        <button class="secondary-button" type="button" data-history-detail-action="back">返回历史记录</button>
        <button class="secondary-button" type="button" data-history-detail-action="edit">修改记录</button>
        <button class="danger-button" type="button" data-history-detail-action="request-delete">删除记录</button>
      </div>
    </div>

    <dialog class="confirm-dialog history-edit-dialog" data-history-edit-dialog aria-labelledby="history-edit-title">
      <form data-history-edit-form>
        <div class="dialog-header">
          <div><p class="eyebrow">编辑本机数据</p><h2 id="history-edit-title">修改记录</h2></div>
          <button class="icon-button" type="button" data-history-detail-action="cancel-edit" aria-label="关闭">×</button>
        </div>
        <div class="history-edit-fields">
          <label><span>用时</span><input type="text" inputmode="numeric" value="${formatTimerTime(record.elapsedMs)}" placeholder="00:00.000" pattern="[0-9]{2}:[0-9]{2}\.[0-9]{3}" data-history-edit-time required></label>
          <label><span>阶数</span><select data-history-edit-order>${TIMER_CUBE_ORDERS.map((order) => `<option value="${order}"${order === record.order ? ' selected' : ''}>${order}×${order}</option>`).join('')}</select></label>
          <label><span>日期时间</span><input type="datetime-local" step="1" value="${toDateTimeLocalValue(record.recordedAt)}" data-history-edit-date required></label>
          <label><span>用户名</span><input type="text" maxlength="80" value="${escapeHtml(record.username)}" data-history-edit-username></label>
        </div>
        <p class="history-edit-error" data-history-edit-error hidden></p>
        <div class="confirm-actions"><button class="secondary-button" type="button" data-history-detail-action="cancel-edit">取消</button><button class="primary-button" type="submit">保存修改</button></div>
      </form>
    </dialog>

    <dialog class="confirm-dialog danger-confirm" data-history-delete-dialog aria-labelledby="history-delete-title">
      <h2 id="history-delete-title">确认删除这条历史记录？</h2>
      <p>第 ${record.serial} 条记录及其全部信息将从此浏览器删除，此操作无法撤销。</p>
      <div class="confirm-actions"><button class="secondary-button" type="button" data-history-detail-action="cancel-delete">取消</button><button class="danger-button" type="button" data-history-detail-action="confirm-delete">确认删除</button></div>
    </dialog>
  </section>`;
}

export class TimerHistoryDetailPage {
  /**
   * @param {any} root
   * @param {string} recordId
   * @param {{ announce?: (message: string) => void, storage?: any }} [options]
   */
  constructor(root, recordId, options = {}) {
    const { announce = (_message) => {}, storage } = options;
    this.root = root;
    this.recordId = recordId;
    this.announce = announce;
    this.storage = storage;
    this.handleClick = (event) => this.#handleClick(event);
    this.handleSubmit = (event) => this.#handleSubmit(event);
    this.root.addEventListener('click', this.handleClick);
    this.root.addEventListener('submit', this.handleSubmit);
    this.#render();
  }

  dispose() {
    this.root.removeEventListener('click', this.handleClick);
    this.root.removeEventListener('submit', this.handleSubmit);
  }

  #render() {
    this.record = getTimerHistoryRecord(this.recordId, this.storage);
    this.root.innerHTML = historyDetailTemplate(this.record);
    if (this.record?.mode === 'dual') {
      const displays = [...this.root.querySelectorAll('[data-timer-display]')];
      renderTimerDisplay(displays[0], this.record.time1);
      renderTimerDisplay(displays[1], this.record.time2);
    } else if (this.record) {
      renderTimerDisplay(this.root.querySelector('[data-timer-display]'), this.record.elapsedMs);
    }
  }

  #handleClick(event) {
    const action = event.target.closest('[data-history-detail-action]')?.dataset.historyDetailAction;
    if (!action) return;
    if (action === 'back') window.location.hash = '#/timer/history';
    else if (action === 'edit') this.root.querySelector('[data-history-edit-dialog]')?.showModal();
    else if (action === 'cancel-edit') this.root.querySelector('[data-history-edit-dialog]')?.close();
    else if (action === 'request-delete') this.root.querySelector('[data-history-delete-dialog]')?.showModal();
    else if (action === 'cancel-delete') this.root.querySelector('[data-history-delete-dialog]')?.close();
    else if (action === 'confirm-delete') {
      if (deleteTimerHistoryRecord(this.recordId, this.storage)) this.announce('历史记录已删除');
      window.location.hash = '#/timer/history';
    }
  }

  #handleSubmit(event) {
    if (!event.target.matches('[data-history-edit-form]')) return;
    event.preventDefault();
    const form = event.target;
    const date = new Date(form.querySelector('[data-history-edit-date]').value);
    const error = form.querySelector('[data-history-edit-error]');
    if (Number.isNaN(date.getTime())) {
      error.textContent = '请选择有效的日期和时间。';
      error.hidden = false;
      return;
    }

    let changes;
    if (this.record?.mode === 'dual') {
      const time1 = parseTimerHistoryTime(form.querySelector('[data-history-edit-time1]').value);
      const time2 = parseTimerHistoryTime(form.querySelector('[data-history-edit-time2]').value);
      if (time1 === null || time2 === null) {
        error.textContent = '双方成绩格式都应为 MM:SS.mmm，最大为 99:99.999。';
        error.hidden = false;
        return;
      }
      changes = {
        player1: form.querySelector('[data-history-edit-player1]').value,
        player2: form.querySelector('[data-history-edit-player2]').value,
        time1,
        time2,
        order: Number(form.querySelector('[data-history-edit-order]').value),
        recordedAt: date,
      };
    } else {
      const elapsedMs = parseTimerHistoryTime(form.querySelector('[data-history-edit-time]').value);
      if (elapsedMs === null) {
        error.textContent = '用时格式应为 MM:SS.mmm，最大为 99:99.999。';
        error.hidden = false;
        return;
      }
      changes = {
        elapsedMs,
        order: Number(form.querySelector('[data-history-edit-order]').value),
        recordedAt: date,
        username: form.querySelector('[data-history-edit-username]').value,
      };
    }

    try {
      const updated = updateTimerHistoryRecord(this.recordId, changes, this.storage);
      if (!updated) {
        error.textContent = '这条记录已不存在，无法保存修改。';
        error.hidden = false;
        return;
      }
      this.#render();
      this.announce('历史记录已更新');
    } catch (cause) {
      error.textContent = `保存失败：${cause?.message ?? String(cause)}`;
      error.hidden = false;
    }
  }
}
